import {
  abs,
  cos,
  float,
  floor,
  Fn,
  hash,
  length,
  mix,
  mx_fractal_noise_float,
  saturate,
  screenUV,
  sin,
  smoothstep,
  uniform,
  vec2,
  vec3,
} from 'three/tsl';
import { Color, type Node } from 'three/webgpu';
import { type AudioFrame } from '../core/audio-frame.js';
import { Gentle } from '../core/gentle.js';
import { Smoothed } from '../core/smoothed.js';

const STAR_CELLS = 190;

/** Gravitational lensing accretion disk around a supermassive black hole. */
function renderBlackHole(
  rel: Node<'vec2'>,
  time: Node<'float'>,
  aspect: Node<'float'>,
): Node<'vec3'> {
  const center = vec2(float(-0.42).mul(aspect), float(0.24));
  const offset = rel.sub(center);
  const dist = length(offset);

  // Razor-sharp event horizon
  const eventHorizon = smoothstep(0.049, 0.052, dist);

  // Sharp photon ring
  const ringDist = abs(dist.sub(0.055));
  const photonRing = smoothstep(0.005, 0, ringDist).mul(3.2);

  // Warped HD accretion disk with relativistic Doppler beaming
  const diskCoord = vec2(offset.x.add(offset.y.mul(0.35)), offset.y.mul(3.2));
  const diskRadius = length(diskCoord);
  const diskOuter = smoothstep(0.24, 0.12, diskRadius);
  const diskInner = smoothstep(0.05, 0.075, diskRadius);
  const diskShape = diskOuter.mul(diskInner);

  const swirlPhase = offset.x.mul(65).add(time.mul(3.8));
  const swirlBands = sin(swirlPhase).mul(0.3).add(0.7);
  const dopplerBoost = smoothstep(0.12, -0.12, offset.x).mul(0.6).add(0.7);
  const accretionGlow = diskShape.mul(swirlBands).mul(dopplerBoost);

  // Color: ultra-vivid fiery solar plasma with white-hot inner boundary
  const fieryCore = vec3(1, 0.55, 0.12).mul(accretionGlow);
  const electricRing = vec3(0.5, 0.9, 1).mul(photonRing);
  const innerHotWhite = smoothstep(0.07, 0.055, dist)
    .mul(smoothstep(0.05, 0.055, dist))
    .mul(vec3(1.4, 1.4, 1.6));

  return fieryCore.add(electricRing).add(innerHotWhite).mul(eventHorizon);
}

/** A ringed gas giant planet with atmospheric banding and crescent lighting. */
function renderPlanet(rel: Node<'vec2'>, aspect: Node<'float'>): Node<'vec3'> {
  const center = vec2(float(0.46).mul(aspect), float(0.25));
  const offset = rel.sub(center);
  const dist = length(offset);

  const planetRadius = float(0.088);
  const planetMask = smoothstep(planetRadius.add(0.0015), planetRadius, dist);

  // High-definition atmospheric cloud bands
  const stormWarp = sin(offset.x.mul(25)).mul(0.02);
  const bandWaveA = sin(offset.y.add(stormWarp).mul(80)).mul(0.3).add(0.7);
  const bandWaveB = cos(offset.y.mul(140)).mul(0.15).add(0.85);
  const stormPattern = bandWaveA.mul(bandWaveB);

  // Crescent sun terminator lighting
  const sunLit = saturate(offset.x.negate().mul(7).add(offset.y.mul(5)).add(0.25));
  const planetSurface = mix(vec3(0.04, 0.16, 0.42), vec3(0.25, 0.82, 1), stormPattern).mul(sunLit);

  // HD Planetary Rings with Cassini Division gap
  const ringCoord = vec2(offset.x.add(offset.y.mul(0.65)), offset.y.mul(3.6));
  const ringRadius = length(ringCoord);
  const ringA = smoothstep(0.23, 0.18, ringRadius).mul(smoothstep(0.165, 0.18, ringRadius));
  const ringB = smoothstep(0.155, 0.13, ringRadius).mul(smoothstep(0.09, 0.11, ringRadius));
  const ringMask = ringA.add(ringB);
  const ringLit = saturate(offset.x.negate().mul(4).add(0.35));
  const rings = vec3(0.55, 0.88, 1).mul(ringMask).mul(ringLit);

  // Atmosphere razor rim glow
  const rimOuter = smoothstep(0.096, 0.089, dist);
  const rimInner = smoothstep(0.086, 0.09, dist);
  const atmoGlow = rimOuter
    .mul(rimInner)
    .mul(vec3(0.3, 0.85, 1))
    .mul(1.5);

  return planetSurface.mul(planetMask).add(rings).add(atmoGlow);
}

/** Towering interstellar dust columns inspired by the Pillars of Creation. */
function renderPillars(p: Node<'vec2'>, time: Node<'float'>, aspect: Node<'float'>): Node<'vec3'> {
  const noiseCoord = vec3(
    p.x.mul(2.2).mul(aspect).add(0.4),
    p.y.mul(1.6).sub(0.25),
    time.mul(0.008),
  );
  const noise = mx_fractal_noise_float(noiseCoord, 5);

  // Sculpted vertical gas columns rising from deep space
  const columnMask = smoothstep(0.32, 0.68, noise).mul(smoothstep(0.95, 0.08, p.y));
  const ionizedRim = smoothstep(0.48, 0.54, noise)
    .mul(smoothstep(0.64, 0.58, noise))
    .mul(2.6);

  // High contrast dust core: deep obsidian rust to glowing amber gold
  const dustCore = mix(vec3(0.12, 0.02, 0.01), vec3(0.95, 0.45, 0.08), noise).mul(columnMask);
  const rimGlow = vec3(0.05, 0.9, 1)
    .mul(ionizedRim)
    .mul(smoothstep(0.95, 0.15, p.y));

  return dustCore.add(rimGlow);
}

/**
 * The hypnotic cosmic sky: a black hole with accretion disk, ringed planet,
 * Pillars of Creation nebula columns, and optical illusion vortex.
 */
export class Atmosphere {
  public readonly node: Node<'vec3'>;
  private readonly uTime = uniform(0);
  private readonly uAspect = uniform(1);
  private readonly uHorizon = uniform(new Color(0x02_08_1a));
  private readonly uZenith = uniform(new Color(0x00_01_04));
  private readonly uAurora = uniform(new Color(0x00_44_88));
  private readonly uEnergy = uniform(0.28);
  private readonly energy = new Gentle(1.2, 0.25, 0.28);
  private readonly horizon = new Color(0x02_08_1a);
  private readonly aurora = new Color(0x00_44_88);
  private readonly drift = new Smoothed(2);

  constructor() {
    this.node = this.build();
    this.setAspect(window.innerWidth / window.innerHeight);
  }

  public setAspect(aspect: number): void {
    this.uAspect.value = aspect;
  }

  /** Ease the sky towards the colour of whatever the field has become. */
  public tint(accent: Color, deltaSeconds: number): void {
    this.horizon.lerp(accent, Math.min(1, deltaSeconds * 0.15));
    this.aurora.lerp(accent, Math.min(1, deltaSeconds * 0.12));
    this.uHorizon.value.setRGB(this.horizon.r * 0.08, this.horizon.g * 0.08, this.horizon.b * 0.14);
    this.uAurora.value.setRGB(this.aurora.r * 0.2, this.aurora.g * 0.2, this.aurora.b * 0.3);
  }

  public update(frame: AudioFrame, elapsedSeconds: number, deltaSeconds: number): void {
    this.uTime.value = elapsedSeconds;
    const level = this.drift.update(frame.level, deltaSeconds);
    this.uEnergy.value = this.energy.update(0.22 + level * 0.22, deltaSeconds);
  }

  private build(): Node<'vec3'> {
    const { uTime, uAspect, uHorizon, uZenith, uAurora, uEnergy } = this;

    const sky = Fn(() => {
      const p = screenUV;
      const rel = p.sub(0.5);
      const relAspect = vec2(rel.x.mul(uAspect), rel.y);
      const dist = length(relAspect);
      const angle = relAspect.y.atan(relAspect.x);

      // Hypnotic Optical Illusion: Animated concentric logarithmic spiral & moiré vortex
      const spiralA = sin(dist.mul(24).sub(angle.mul(6)).sub(uTime.mul(1.4)));
      const spiralB = cos(dist.mul(16).add(angle.mul(4)).add(uTime.mul(0.9)));
      const moire = spiralA.mul(spiralB);

      // Op-Art geometric warping distortion waves
      const innerX = relAspect.y.mul(14).add(uTime.mul(1.1));
      const waveX = sin(relAspect.x.mul(18).add(sin(innerX)));

      const innerY = relAspect.x.mul(14).sub(uTime.mul(1.1));
      const waveY = cos(relAspect.y.mul(18).add(cos(innerY)));
      const illusionPattern = smoothstep(
        -0.4,
        0.8,
        moire.add(waveX.mul(0.35)).add(waveY.mul(0.35)),
      );

      // Deep space backdrop
      const deepAbyss = uZenith;
      const cobaltWave = mix(uHorizon, uAurora, illusionPattern.mul(0.7));
      const illusionGlow = mix(
        deepAbyss,
        cobaltWave,
        smoothstep(1.2, 0.05, dist).mul(illusionPattern),
      );

      // HD Celestial Objects
      const blackHoleFx = renderBlackHole(relAspect, uTime, uAspect);
      const planetFx = renderPlanet(relAspect, uAspect);
      const pillarsFx = renderPillars(p, uTime, uAspect);

      // Stars: one candidate per screen cell
      const grid = vec2(p.x.mul(uAspect), p.y).mul(STAR_CELLS);
      const cell = floor(grid);
      const pick = hash(cell.x.mul(157).add(cell.y.mul(31)).add(11));
      const brightness = hash(cell.x.mul(71).add(cell.y.mul(199)).add(5));
      const offset = hash(cell.x.mul(11).add(cell.y.mul(7)).add(3));
      const centre = vec2(offset, brightness).mul(0.6).add(0.2);
      const dot = smoothstep(0.4, 0, length(grid.sub(cell).sub(centre)));
      const twinkle = sin(uTime.mul(0.8).add(brightness.mul(40)))
        .mul(0.25)
        .add(0.75);
      const star = smoothstep(0.984, 1, pick).mul(brightness).mul(twinkle).mul(dot).mul(0.9);

      // Render illusion with energy modulation, while celestial bodies render with HD crispness
      const celestialScene = illusionGlow
        .mul(uEnergy.mul(1.15))
        .add(pillarsFx)
        .add(blackHoleFx)
        .add(planetFx)
        .add(vec3(star));

      const vignette = smoothstep(1.4, 0.2, length(p.sub(0.5)).mul(1.5));
      return celestialScene.mul(vignette);
    });

    return sky();
  }
}
